/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionLoopSelect';

const ActionLoopSelectManager: ActionManagerFn<'ActionLoopSelect'> = (
  platform: Platform,
): ActionManager<'ActionLoopSelect'> => {
  return {
    ...baseActionManager(platform, 'ActionLoopSelect'),
    defaultValue,
    props: {
      ...baseActionProps,
    },
    name: 'Select Record',
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return ['ActionLoopRecords'].includes(parent.type);
    },
    icon: { iconName: 'check-square' },
    description: `**Overview**<br/>
The "Select Record" (Loop) action is a context-aware state management component designed specifically for use within an iteration cycle. It serves as the functional counterpart to ActionLoopDeselect, allowing developers to programmatically add the current record being processed by the parent loop to the selection set.<br/><br/>
This component operates without explicit configuration properties because it automatically targets the record currently held in the loop's iteration context. It is strictly scoped to work only inside an ActionLoopRecords container, ensuring it always has a valid reference to act upon.

**Key Features**<br/>
* **Context-Driven Execution:** Automatically identifies and selects the specific record currently being iterated over, removing the need for variable mapping or ID lookup.
* **Strict Parent Scope:** Enforces usage validity via isAllowedInsideParent, restricting the component to be a child of ActionLoopRecords only.
* **Zero Configuration:** Inherits all behavior from baseActionManager and requires no inputs from the developer to function.

**Properties**<br/>
* **Base Properties:** This component utilizes ...baseActionProps exclusively, accepting only the standard configuration options available to all actions.

**Use Cases**<br/>
* **Bulk Selection Logic:** Iterating through "All" records in a store and using this action to select every record that meets a specific criteria (e.g., "Select all employees where Department == 'Sales'").
* **Smart Filtering:** A "Select All Overdue" button that loops through invoices and selects those where the due date is in the past.`,
  };
};

export default ActionLoopSelectManager;
